import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Language as WebsiteIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSchools } from '../../../hooks/useSchools';
import SchoolWebsiteFormModal from '../../../components/Modals/SchoolWebsiteFormModal';
import { SchoolWebsiteData } from '../../../hooks/useSchools';

const SchoolWebsiteManagement: React.FC = () => {
  const { t } = useTranslation();
  const { schoolsQuery } = useSchools(false, { enableQuery: true });
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentWebsite, setCurrentWebsite] = useState<SchoolWebsiteData | undefined>(undefined);
  const [selectedSchool, setSelectedSchool] = useState<any>(null);

  const handleOpenAddModal = (school: any) => {
    setSelectedSchool(school);
    setIsEdit(false);
    setCurrentWebsite(undefined);
    setModalOpen(true);
  };

  const handleOpenEditModal = (website: SchoolWebsiteData, school: any) => {
    setSelectedSchool(school);
    setIsEdit(true);
    setCurrentWebsite(website);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSchool(null);
  };

  const handleSubmit = (websiteData: SchoolWebsiteData) => {
    // Here you would call your API to save the website data
    handleCloseModal();
  };

  const getServiceStatus = (school: any) => {
    const services = [];
    if (school.emailServiceEnabled) services.push({ type: 'email', label: t('email_service'), icon: <EmailIcon /> });
    if (school.whatsappServiceEnabled) services.push({ type: 'whatsapp', label: t('whatsapp_service'), icon: <WhatsAppIcon /> });
    if (school.websiteServiceEnabled) services.push({ type: 'website', label: t('website_service'), icon: <WebsiteIcon /> });
    return services;
  };

  if (schoolsQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  const schoolsWithServices = schoolsQuery.data?.filter(school => 
    school.emailServiceEnabled || school.whatsappServiceEnabled || school.websiteServiceEnabled
  ) || [];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight="bold" sx={{ mb: 3 }}>
        {t('school_website_management')}
      </Typography>

      {schoolsWithServices.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          {t('no_schools_with_services')}
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {schoolsWithServices.map((school) => (
            <Grid item xs={12} md={6} lg={4} key={school.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                    {school.nameofSchool}
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                      {t('enabled_services')}:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {getServiceStatus(school).map((service, index) => (
                        <Chip
                          key={index}
                          icon={service.icon}
                          label={service.label}
                          size="small"
                          color={service.type === 'website' ? 'primary' : service.type === 'email' ? 'success' : 'info'}
                        />
                      ))}
                    </Box>
                  </Box>

                  {school.customWebsiteUrl && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {t('website_url')}:
                      </Typography>
                      <Typography variant="body2" color="primary">
                        <a href={school.customWebsiteUrl} target="_blank" rel="noopener noreferrer">
                          {school.customWebsiteUrl}
                        </a>
                      </Typography>
                    </Box>
                  )}
                </CardContent>

                <CardActions>
                  {school.websiteServiceEnabled && (
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenAddModal(school)}
                    >
                      {t('create_website')}
                    </Button>
                  )}
                  <Button
                    size="small"
                    startIcon={<ViewIcon />}
                    onClick={() => window.open(school.customWebsiteUrl, '_blank')}
                    disabled={!school.customWebsiteUrl}
                  >
                    {t('view_website')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <SchoolWebsiteFormModal
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentWebsite}
        onSubmit={handleSubmit}
        isEdit={isEdit}
        schoolId={selectedSchool?.id || 0}
      />
    </Box>
  );
};

export default SchoolWebsiteManagement; 